<script setup>
import OrderProgress from '@/pages/order/components/OrderProgress.vue'
import { useSelfOrderStore } from '@/stores/selfOrder'

definePage({
  meta: {
    layout: 'blank',
    public: true,
  },
})

const route = useRoute()
const router = useRouter()
const store = useSelfOrderStore()

const orderId = computed(() => route.query.orderId)

const isLoading = ref(true)
const errorMessage = ref('')
const qrString = ref('')
const expiredAt = ref(null)
const startedAt = ref(null)
const qrImageDataUrl = ref('')
const isPaid = ref(false)

let pollTimer = null

const secondsLeft = ref(0)
const totalSeconds = ref(1)
let countdownTimer = null

const formatCountdown = seconds => {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0')
  const s = Math.floor(seconds % 60).toString().padStart(2, '0')

  return `${m}:${s}`
}

const countdownProgress = computed(() => {
  if (!totalSeconds.value)
    return 0

  return Math.max(0, Math.min(1, secondsLeft.value / totalSeconds.value))
})

const circleCircumference = 2 * Math.PI * 54

const startCountdown = () => {
  if (!expiredAt.value)
    return

  startedAt.value = Date.now()
  totalSeconds.value = Math.max(1, Math.round((new Date(expiredAt.value).getTime() - startedAt.value) / 1000))

  clearInterval(countdownTimer)
  countdownTimer = setInterval(() => {
    const diff = (new Date(expiredAt.value).getTime() - Date.now()) / 1000

    secondsLeft.value = Math.max(0, Math.round(diff))
    if (secondsLeft.value <= 0)
      clearInterval(countdownTimer)
  }, 1000)
}

// Render QR pakai package `qrcode` (npm install qrcode) kalau tersedia di project.
// Fallback: tampilkan qr_string mentah supaya kasir tetap bisa proses manual
// kalau library belum terpasang.
const renderQr = async text => {
  try {
    const QRCode = await import('qrcode')

    qrImageDataUrl.value = await QRCode.toDataURL(text, { margin: 1, width: 260 })
  }
  catch (e) {
    // Package 'qrcode' belum ter-install — biarkan fallback teks yang tampil di template.
    qrImageDataUrl.value = ''
  }
}

const requestQris = async () => {
  isLoading.value = true
  errorMessage.value = ''

  try {
    const data = await store.payQris(orderId.value)

    // Addendum 06 (iPaymu): provider ini tidak mengembalikan raw QRIS
    // string, cuma invoice_url (halaman checkout hosted iPaymu). Customer
    // sudah membuka halaman ini di HP-nya sendiri, jadi redirect penuh
    // (bukan scan ulang QR dari layar sendiri) — pola sama seperti redirect
    // invoice langganan Xendit di settings/[tab].vue.
    if (!data.qr_string && data.invoice_url) {
      window.location.href = data.invoice_url

      return
    }

    qrString.value = data.qr_string
    expiredAt.value = data.expired_at
    startCountdown()
    await renderQr(data.qr_string)
  }
  catch (err) {
    errorMessage.value = err?.data?.message || err?.message || 'Gagal membuat QR pembayaran.'
  }
  finally {
    isLoading.value = false
  }
}

// TODO Modul 18: ganti polling ini ke Pusher/Echo (event OrderPaid) begitu
// broadcast realtime tersedia. Untuk Modul 15, cek status via polling.
const pollOrderStatus = async () => {
  try {
    const orders = await store.fetchOrders()
    const current = orders.find(o => String(o.id) === String(orderId.value))

    if (current && current.status !== 'pending') {
      isPaid.value = true
      clearInterval(pollTimer)
      clearInterval(countdownTimer)
      router.replace({ name: 'order-session-token-status', params: { sessionToken: route.params.sessionToken } })
    }
  }
  catch {
    // diamkan — polling berikutnya akan coba lagi
  }
}

onMounted(async () => {
  if (!orderId.value) {
    errorMessage.value = 'Order tidak ditemukan.'
    isLoading.value = false
    return
  }

  await requestQris()
  pollTimer = setInterval(pollOrderStatus, 5000)
})

onBeforeUnmount(() => {
  clearInterval(pollTimer)
  clearInterval(countdownTimer)
})
</script>

<template>
  <div class="payment-screen d-flex flex-column align-center pa-4">
    <div class="payment-blob" />

    <div class="px-2" style="inline-size: 100%; max-inline-size: 420px;">
      <OrderProgress :step="4" />
    </div>

    <VCard class="payment-card pa-6 text-center mt-2" max-width="420" elevation="0">
      <template v-if="isLoading">
        <VProgressCircular indeterminate color="primary" size="48" class="mb-4" />
        <p class="text-body-2 text-medium-emphasis mb-0">
          Menyiapkan QR pembayaran…
        </p>
      </template>

      <template v-else-if="errorMessage">
        <div class="payment-error-icon mb-5">
          <VIcon icon="ri-error-warning-line" size="36" color="error" />
        </div>
        <p class="text-body-2 text-medium-emphasis mb-6">
          {{ errorMessage }}
        </p>
        <VBtn block rounded="lg" @click="requestQris">
          Coba Lagi
        </VBtn>
      </template>

      <template v-else>
        <h5 class="text-h5 font-weight-bold mb-1">
          Scan untuk Bayar
        </h5>
        <p class="text-body-2 text-medium-emphasis mb-5">
          Buka aplikasi e-wallet/mobile banking Anda dan scan QRIS di bawah ini
        </p>

        <div class="qr-frame mb-5">
          <span class="qr-corner qr-corner--tl" />
          <span class="qr-corner qr-corner--tr" />
          <span class="qr-corner qr-corner--bl" />
          <span class="qr-corner qr-corner--br" />
          <VImg
            v-if="qrImageDataUrl"
            :src="qrImageDataUrl"
            width="230"
            height="230"
            class="mx-auto"
          />
          <VAlert v-else type="warning" variant="tonal" class="text-start" rounded="lg">
            Library QR belum terpasang di project (<code>npm install qrcode</code>). Kode mentah untuk kasir:
            <div class="text-caption font-weight-medium mt-1" style="word-break: break-all;">
              {{ qrString }}
            </div>
          </VAlert>
        </div>

        <template v-if="secondsLeft > 0">
          <div class="countdown-ring mb-4">
            <svg viewBox="0 0 120 120" width="88" height="88">
              <circle cx="60" cy="60" r="54" class="countdown-track" />
              <circle
                cx="60" cy="60" r="54"
                class="countdown-progress"
                :stroke-dasharray="circleCircumference"
                :stroke-dashoffset="circleCircumference * (1 - countdownProgress)"
              />
            </svg>
            <div class="countdown-label">
              <span class="text-body-2 font-weight-bold">{{ formatCountdown(secondsLeft) }}</span>
              <span class="text-caption text-medium-emphasis">tersisa</span>
            </div>
          </div>
        </template>
        <VAlert v-else type="error" variant="tonal" class="mb-4" rounded="lg">
          QR pembayaran sudah kedaluwarsa. Silakan buat ulang.
          <template #append>
            <VBtn size="small" variant="text" color="error" @click="requestQris">
              Buat Ulang
            </VBtn>
          </template>
        </VAlert>

        <div class="d-flex align-center justify-center gap-2 text-medium-emphasis">
          <span class="poll-dot" />
          <span class="text-caption">Menunggu konfirmasi pembayaran…</span>
        </div>
      </template>
    </VCard>
  </div>
</template>

<style scoped>
.payment-screen {
  position: relative;
  min-block-size: 100vh;
  overflow: hidden;
  background: rgb(var(--v-theme-background));
}

.payment-blob {
  position: absolute;
  inset-block-start: -140px;
  inset-inline-start: 50%;
  transform: translateX(-50%);
  inline-size: 340px;
  block-size: 340px;
  border-radius: 50%;
  background: rgb(var(--v-theme-primary));
  opacity: 0.12;
  filter: blur(50px);
}

.payment-card {
  position: relative;
  z-index: 1;
  border-radius: 24px;
  background: rgb(var(--v-theme-surface));
  box-shadow: 0 24px 48px -24px rgba(var(--v-theme-on-surface), 0.25);
}

.qr-frame {
  position: relative;
  display: inline-block;
  padding: 16px;
  border-radius: 20px;
  background: rgba(var(--v-theme-primary), 0.04);
}

.qr-corner {
  position: absolute;
  inline-size: 20px;
  block-size: 20px;
  border-color: rgb(var(--v-theme-primary));
}

.qr-corner--tl { inset-block-start: 4px; inset-inline-start: 4px; border-block-start: 3px solid; border-inline-start: 3px solid; border-radius: 6px 0 0 0; }
.qr-corner--tr { inset-block-start: 4px; inset-inline-end: 4px; border-block-start: 3px solid; border-inline-end: 3px solid; border-radius: 0 6px 0 0; }
.qr-corner--bl { inset-block-end: 4px; inset-inline-start: 4px; border-block-end: 3px solid; border-inline-start: 3px solid; border-radius: 0 0 0 6px; }
.qr-corner--br { inset-block-end: 4px; inset-inline-end: 4px; border-block-end: 3px solid; border-inline-end: 3px solid; border-radius: 0 0 6px 0; }

.countdown-ring {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-inline: auto;
  inline-size: 88px;
  block-size: 88px;
}

.countdown-ring svg {
  transform: rotate(-90deg);
}

.countdown-track {
  fill: none;
  stroke: rgba(var(--v-theme-on-surface), 0.1);
  stroke-width: 8;
}

.countdown-progress {
  fill: none;
  stroke: rgb(var(--v-theme-primary));
  stroke-width: 8;
  stroke-linecap: round;
  transition: stroke-dashoffset 1s linear;
}

.countdown-label {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.payment-error-icon {
  inline-size: 68px;
  block-size: 68px;
  margin-inline: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(var(--v-theme-error), 0.1);
}

.poll-dot {
  inline-size: 8px;
  block-size: 8px;
  border-radius: 50%;
  background: rgb(var(--v-theme-primary));
  animation: poll-pulse 1.4s ease-in-out infinite;
}

@keyframes poll-pulse {
  0%, 100% { opacity: 0.3; transform: scale(0.8); }
  50% { opacity: 1; transform: scale(1.2); }
}

@media (prefers-reduced-motion: reduce) {
  .poll-dot { animation: none; }
  .countdown-progress { transition: none; }
}
</style>
